import * as Sentry from "@sentry/react";

import { env } from "@/env";
import { enrichSentryEventWithAxios } from "@/lib/sentryAxios";

/**
 * Every helper in this module is a no-op without `VITE_SENTRY_DSN`, so a
 * deployment that never set it reports nothing at all — and nothing says so.
 * That is indistinguishable from "no errors happened", which is how a stream of
 * handled 4xx failures on the create/update forms stayed invisible. One console
 * warning at boot makes the disabled state answerable from the browser console
 * instead of by reading this file.
 */
export const initSentry = () => {
  if (!env.sentryDsn) {
    console.warn(
      "[sentry] disabled: VITE_SENTRY_DSN is not set — no errors will be reported from this build."
    );
    return;
  }

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.sentryEnvironment,
    release: env.sentryRelease,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: env.sentryEnvironment === "production" ? 0.1 : 1.0,
    sendDefaultPii: false,
    beforeSend: enrichSentryEventWithAxios,
  });
};

export const setSentryUser = (user: { id: string; email?: string | null } | null) => {
  if (!env.sentryDsn) return;

  if (user === null) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
};

export const captureSentryException = (
  error: unknown,
  context?: Parameters<typeof Sentry.captureException>[1]
) => {
  if (!env.sentryDsn) return;

  Sentry.captureException(error, context);
};

type FormErrorContext = {
  form: string;
  action: "create" | "update" | "delete" | "lookup" | "generate";
  extra?: Record<string, unknown>;
};

export const addSentryBreadcrumb = (breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]) => {
  if (!env.sentryDsn) return;

  Sentry.addBreadcrumb(breadcrumb);
};

export const trackAction = (action: string, data?: Record<string, unknown>) => {
  if (!env.sentryDsn) return;

  Sentry.addBreadcrumb({
    category: "ui.action",
    level: "info",
    message: action,
    data,
  });
};

export const captureFormError = (error: unknown, context: FormErrorContext) => {
  if (!env.sentryDsn) return;

  Sentry.captureException(error, {
    tags: { source: "form", form: context.form, action: context.action },
    extra: context.extra,
  });
};
